import { createSlice } from "@reduxjs/toolkit";
import { API_STATUS } from "../../../constants/apiStatus";
import {
  getAllMealsAction,
  getUserMealsAction,
  getMealByIdAction,
  createMealAction,
  updateMealAction,
  deleteMealAction,
} from "./mealsSliceActions";
import { mealsSliceInitialState } from "./mealsSliceInitialState";

const initialState = mealsSliceInitialState;

const mealsSlice = createSlice({
  name: "meals",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedMeal: (state, action) => {
      state.selectedMeal = action.payload;
    },
    clearSelectedMeal: (state) => {
      state.selectedMeal = null;
    },
    resetMealsState: () => initialState,
  },
  extraReducers: (builder) => {
    // Get All Meals
    builder.addCase(getAllMealsAction.fulfilled, (state, action) => {
      state.apiStatus.getAllMeals = API_STATUS.COMPLETED;
      state.meals = action.payload.meals;
      state.pagination = {
        currentPage: action.payload.currentPage,
        totalPages: action.payload.totalPages,
        totalMeals: action.payload.totalMeals,
        limit: action.payload.limit,
      };
      state.error = null;
    });
    builder.addCase(getAllMealsAction.pending, (state) => {
      state.apiStatus.getAllMeals = API_STATUS.STARTED;
      state.error = null;
    });
    builder.addCase(getAllMealsAction.rejected, (state, action) => {
      state.apiStatus.getAllMeals = API_STATUS.FAILED;
      state.error = action.payload;
    });

    // Get User Meals
    builder.addCase(getUserMealsAction.fulfilled, (state, action) => {
      state.apiStatus.getUserMeals = API_STATUS.COMPLETED;
      // API returns an object { success, meals: [...], date, userId }
      // We only store the meals array for the UI
      if (Array.isArray(action.payload)) {
        state.userMeals = action.payload;
      } else if (action.payload && Array.isArray(action.payload.meals)) {
        state.userMeals = action.payload.meals;
      } else {
        state.userMeals = [];
      }
      state.error = null;
    });
    builder.addCase(getUserMealsAction.pending, (state) => {
      state.apiStatus.getUserMeals = API_STATUS.STARTED;
      state.error = null;
    });
    builder.addCase(getUserMealsAction.rejected, (state, action) => {
      state.apiStatus.getUserMeals = API_STATUS.FAILED;
      state.error = action.payload;
    });

    // Get Meal By ID
    builder.addCase(getMealByIdAction.fulfilled, (state, action) => {
      state.apiStatus.getMealById = API_STATUS.COMPLETED;
      state.selectedMeal = action.payload;
      state.error = null;
    });
    builder.addCase(getMealByIdAction.pending, (state) => {
      state.apiStatus.getMealById = API_STATUS.STARTED;
      state.error = null;
    });
    builder.addCase(getMealByIdAction.rejected, (state, action) => {
      state.apiStatus.getMealById = API_STATUS.FAILED;
      state.error = action.payload;
    });

    // Create Meal
    builder.addCase(createMealAction.fulfilled, (state, action) => {
      state.apiStatus.createMeal = API_STATUS.COMPLETED;
      state.meals.unshift(action.payload);
      state.userMeals.unshift(action.payload);
      state.error = null;
    });
    builder.addCase(createMealAction.pending, (state) => {
      state.apiStatus.createMeal = API_STATUS.STARTED;
      state.error = null;
    });
    builder.addCase(createMealAction.rejected, (state, action) => {
      state.apiStatus.createMeal = API_STATUS.FAILED;
      state.error = action.payload;
    });

    // Update Meal
    builder.addCase(updateMealAction.fulfilled, (state, action) => {
      state.apiStatus.updateMeal = API_STATUS.COMPLETED;
      const mealIndex = state.meals.findIndex(
        (meal) => meal.id === action.payload.id
      );
      if (mealIndex !== -1) {
        state.meals[mealIndex] = action.payload;
      }
      const userMealIndex = state.userMeals.findIndex(
        (meal) => meal.id === action.payload.id
      );
      if (userMealIndex !== -1) {
        state.userMeals[userMealIndex] = action.payload;
      }
      if (state.selectedMeal && state.selectedMeal.id === action.payload.id) {
        state.selectedMeal = action.payload;
      }
      state.error = null;
    });
    builder.addCase(updateMealAction.pending, (state) => {
      state.apiStatus.updateMeal = API_STATUS.STARTED;
      state.error = null;
    });
    builder.addCase(updateMealAction.rejected, (state, action) => {
      state.apiStatus.updateMeal = API_STATUS.FAILED;
      state.error = action.payload;
    });

    // Delete Meal
    builder.addCase(deleteMealAction.fulfilled, (state, action) => {
      state.apiStatus.deleteMeal = API_STATUS.COMPLETED;
      state.meals = state.meals.filter((meal) => meal.id !== action.payload);
      state.userMeals = state.userMeals.filter(
        (meal) => meal.id !== action.payload
      );
      if (state.selectedMeal && state.selectedMeal.id === action.payload) {
        state.selectedMeal = null;
      }
      state.error = null;
    });
    builder.addCase(deleteMealAction.pending, (state) => {
      state.apiStatus.deleteMeal = API_STATUS.STARTED;
      state.error = null;
    });
    builder.addCase(deleteMealAction.rejected, (state, action) => {
      state.apiStatus.deleteMeal = API_STATUS.FAILED;
      state.error = action.payload;
    });
  },
});

export const {
  clearError,
  setSelectedMeal,
  clearSelectedMeal,
  resetMealsState,
} = mealsSlice.actions;

export default mealsSlice.reducer;
